import React, { useState } from "react";
import { ChevronDown, Plus, UserRound } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useSubjects } from "../SubjectContext";
import { track } from "../lib/analytics";
import { profileToggleSegments } from "../lib/profileToggleLabel";
import { Switch } from "./ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { SubjectAvatar } from "./SubjectSwitcher";
import AddPersonModal from "./AddPersonModal";

export default function CaseForChip({
  includeProfile,
  onIncludeChange,
  onReview,
  onOpenSafety,
  headerExtra = null,
}) {
  const { t, language } = useLanguage();
  const {
    subjects,
    activeSubject,
    caseOneOff,
    setActiveSubjectId,
    selectOneOffForCase,
    clearCaseOneOff,
    atLimit,
    refreshSubjects,
  } = useSubjects();
  const [addOpen, setAddOpen] = useState(false);

  const label = caseOneOff
    ? t("subjects.someoneElse")
    : activeSubject?.display_name || t("subjects.me");

  const showProfileControls =
    !caseOneOff && Boolean(activeSubject?.tier_a_complete);

  return (
    <div className="space-y-2" data-testid="case-for-block">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-sm md:text-base text-muted-foreground">
          {t("subjects.caseFor")}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="case-for-chip"
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-sm hover:bg-muted/50"
            >
              {caseOneOff ? (
                <UserRound className="h-4 w-4 text-muted-foreground" />
              ) : (
                <SubjectAvatar subject={activeSubject} size="xs" />
              )}
              <span className="font-medium max-w-[10rem] truncate">{label}</span>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-60">
            {subjects.map((s) => (
              <DropdownMenuItem
                key={s.subject_id}
                className="gap-2"
                data-testid={`case-for-subject-${s.subject_id}`}
                onClick={() => {
                  clearCaseOneOff();
                  setActiveSubjectId(s.subject_id);
                  track("case_subject_selected", {
                    subject_kind: s.kind,
                    subject_mode: "subject",
                  });
                }}
              >
                <SubjectAvatar subject={s} size="sm" />
                <span className="truncate">
                  {s.display_name}
                  {s.age_display ? ` · ${s.age_display}` : ""}
                </span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-2"
              data-testid="case-for-one-off"
              onClick={() => {
                selectOneOffForCase();
                track("case_subject_selected", {
                  subject_kind: null,
                  subject_mode: "one_off",
                });
              }}
            >
              <UserRound className="h-4 w-4" />
              {t("subjects.someoneElse")}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={atLimit}
              className="gap-2"
              onClick={() => !atLimit && setAddOpen(true)}
            >
              <Plus className="h-4 w-4" />
              {t("subjects.addPerson")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {headerExtra}
      </div>

      {caseOneOff && (
        <p className="text-sm text-muted-foreground">{t("subjects.oneOffHint")}</p>
      )}

      {!caseOneOff && activeSubject && !activeSubject.tier_a_complete && (
        <button
          type="button"
          className="text-sm text-primary hover:underline"
          onClick={() => onReview?.(activeSubject)}
        >
          {t("subjects.addAgeSex").replace("{name}", activeSubject.display_name)}
        </button>
      )}

      {showProfileControls && activeSubject && (
        <div className="space-y-1.5">
          <div className="flex items-start gap-2.5">
            <Switch
              id="use-profile-toggle"
              className="mt-0.5"
              data-testid="use-profile-toggle"
              checked={includeProfile}
              onCheckedChange={(checked) => onIncludeChange?.(checked)}
            />
            <span className="text-sm md:text-base leading-snug">
              {profileToggleSegments(t, language, activeSubject).map(
                (segment, index) =>
                  segment.type === "safety" ? (
                    <button
                      key={`${segment.field}-${index}`}
                      type="button"
                      className="text-primary underline underline-offset-2 hover:opacity-80"
                      data-testid={`profile-safety-link-${segment.field}`}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onOpenSafety?.();
                      }}
                    >
                      {segment.text}
                    </button>
                  ) : (
                    <label
                      key={`text-${index}`}
                      htmlFor="use-profile-toggle"
                      className="cursor-pointer"
                    >
                      {segment.text}
                    </label>
                  ),
              )}
            </span>
          </div>
          <button
            type="button"
            className="text-sm text-primary hover:underline"
            data-testid="edit-profile-link"
            onClick={() => onReview?.(activeSubject)}
          >
            {t("subjects.editProfile")}
          </button>
        </div>
      )}

      <AddPersonModal
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={async (created) => {
          await refreshSubjects();
          if (created?.subject_id) {
            clearCaseOneOff();
            setActiveSubjectId(created.subject_id);
          }
        }}
      />
    </div>
  );
}
